import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

/**
 * Custom sync validators for the dynamic form system
 */

/**
 * Required validator factory
 */
export function requiredValidator(params?: any): ValidatorFn {
  console.log('[requiredValidator] Validators.required:', Validators.required);
  return Validators.required;
}

/**
 * Email validator factory with comprehensive regex pattern
 */
export function emailValidator(params?: any): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    // Comprehensive email regex pattern
    // Supports: standard emails, subdomains, plus addressing, special characters
    const emailPattern = /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

    const value = control.value.trim();
    const valid = emailPattern.test(value);

    return valid ? null : { email: { value: control.value } };
  };
}

/**
 * Min length validator factory
 */
export function minLengthValidator(params?: any): ValidatorFn {
  const length = params?.length || params?.minLength || 0;
  return Validators.minLength(length);
}

/**
 * Max length validator factory
 */
export function maxLengthValidator(params?: any): ValidatorFn {
  const length = params?.length || params?.maxLength || Infinity;
  return Validators.maxLength(length);
}

/**
 * Min validator factory (for numbers)
 */
export function minValidator(params?: any): ValidatorFn {
  const min = params?.min || params?.value || 0;
  return Validators.min(min);
}

/**
 * Max validator factory (for numbers)
 */
export function maxValidator(params?: any): ValidatorFn {
  const max = params?.max || params?.value || Infinity;
  return Validators.max(max);
}

/**
 * Pattern validator factory
 */
export function patternValidator(params?: any): ValidatorFn {
  const pattern = params?.pattern || params?.value || '';
  return Validators.pattern(pattern);
}

/**
 * Custom phone number validator
 */
export function phoneValidator(params?: any): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const phonePattern = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/;
    const valid = phonePattern.test(control.value);

    return valid ? null : { phone: { value: control.value } };
  };
}

/**
 * URL validator
 */
export function urlValidator(params?: any): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    try {
      new URL(control.value);
      return null;
    } catch {
      return { url: { value: control.value } };
    }
  };
}

/**
 * Alphanumeric validator
 */
export function alphanumericValidator(params?: any): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const alphanumericPattern = /^[a-zA-Z0-9]+$/;
    const valid = alphanumericPattern.test(control.value);

    return valid ? null : { alphanumeric: { value: control.value } };
  };
}

/**
 * Numeric only validator
 */
export function numericValidator(params?: any): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const numericPattern = /^[0-9]+$/;
    const valid = numericPattern.test(control.value);

    return valid ? null : { numeric: { value: control.value } };
  };
}

/**
 * Match field validator (e.g., password confirmation)
 */
export function matchFieldValidator(params?: any): ValidatorFn {
  const fieldToMatch = params?.fieldToMatch || params?.field;

  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value || !fieldToMatch) {
      return null;
    }

    const parent = control.parent;
    if (!parent) {
      return null;
    }

    const matchControl = parent.get(fieldToMatch);
    if (!matchControl) {
      return null;
    }

    const match = control.value === matchControl.value;
    return match ? null : { matchField: { field: fieldToMatch } };
  };
}

/**
 * File type validator
 */
export function fileTypeValidator(params?: any): ValidatorFn {
  const allowedTypes = params?.allowedTypes || params?.types || [];

  return (control: AbstractControl): ValidationErrors | null => {
    const file = control.value;

    if (!file) {
      return null;
    }

    if (file instanceof File) {
      const fileType = file.type;
      const valid = allowedTypes.some((type: string) => fileType.includes(type));

      return valid ? null : { fileType: { allowedTypes, actualType: fileType } };
    }

    return null;
  };
}

/**
 * File size validator
 */
export function fileSizeValidator(params?: any): ValidatorFn {
  const maxSizeMB = params?.maxSizeMB || params?.maxSize || 10;
  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  return (control: AbstractControl): ValidationErrors | null => {
    const file = control.value;

    if (!file) {
      return null;
    }

    if (file instanceof File) {
      const valid = file.size <= maxSizeBytes;
      return valid ? null : { fileSize: { maxSizeMB, actualSizeMB: (file.size / 1024 / 1024).toFixed(2) } };
    }

    return null;
  };
}
